import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS,
         HttpClient } from '@angular/common/http';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule }   from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { RouterModule, Routes } from '@angular/router';
import { Ng2ImgMaxModule } from 'ng2-img-max';
import { registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import localeDE from '@angular/common/locales/de';
import localeFR from '@angular/common/locales/fr';

import { CoreModule } from './core.module';

import { AppComponent } from './Components/app.component';
import { HomeComponent } from './Components/home';
import { NavComponent } from './Components/nav';
import { AlertComponent } from './Components/alert';

import { CartDetailComponent } from './Components/cart.detail';
import { ProductDetailComponent } from './Components/product.detail';
import { ProductPreviewComponent } from './Components/product.preview';
import { ProductSummaryComponent } from './Components/product.summary';
import { ProductCreateComponent } from './Components/product.create';
import { ProductUpdateComponent } from './Components/product.update';
import { ProductListComponent } from './Components/product.list';
import { HighlightsComponent } from './Components/highlights';
import { ProductFilterComponent } from './Components/product.filter';
import { ProductPaginationComponent } from './Components/product.pagination';
import { ProductSearchComponent } from './Components/product.search';
import { ActiveFilterComponent } from './Components/activefilter';
import { BrandListComponent } from './Components/brand.list';
import { BrandDetailComponent } from './Components/brand.detail';
import { CategoryListComponent } from './Components/category.list';
import { CollectionListComponent } from './Components/collection.list';
import { LoginComponent } from './Components/login';
import { RequestPwdComponent } from './Components/requestpwd';
import { PasswordResetComponent } from './Components/password.reset';
import { RegisterComponent } from './Components/register';
import { ProfileComponent } from './Components/profile';
import { ProfileOrdersComponent } from './Components/profile.orders';
import { ProfileBankaccountComponent } from './Components/profile.bankaccount';
import { AddressComponent } from './Components/address';
import { InboxComponent } from './Components/inbox';
import { ProfilePasswordComponent } from './Components/profile.password';
import { ImageFieldComponent } from './Components/imagefield.component';
import { PageComponent } from './Components/page';
import { PageNotFoundComponent } from './Components/pagenotfound';
import { CheckoutComponent } from './Components/checkout';
import { OrderComponent } from './Components/order';
import { SpinnerComponent } from './Components/spinner';
import { CarouselComponent } from './Components/carousel';
import { FooterComponent } from './Components/footer';

import { OnlyNumber } from './Directives/onlynumber.directive';
import { RouteDirective } from './Directives/route.directive';

import { ProductService } from './Services/product';
import { ProductFilterService } from './Services/productfilter';
import { ProductDetailService } from './Services/product.detail';
import { BrandService } from './Services/brand';
import { ColorService } from './Services/color';
import { SizeService } from './Services/size';
import { StateService } from './Services/state';
import { FabricService } from './Services/fabric';
import { CategoryService } from './Services/category';
import { CollectionService } from './Services/collection';
import { PageService } from './Services/page';
import { PageImageService } from './Services/pageimage';
import { UserService } from './Services/user';
import { Config } from './Services/config';
import { AuthService } from './Services/auth';
import { LanguageService } from './Services/lang';
import { MessageService } from './Services/message';
import { SpinnerService } from './Services/spinner';
import { ImageUtils } from './Services/imageutils';
import { GoogleAnalyticsEventsService } from './Services/google-analytics';
import { MetaService } from './Services/meta';

import { TranslatePipe } from './Pipes/translate';
import { NoSanitizePipe } from './Pipes/nosanitize';

import { AppRoutes } from './Routes/app.routes';

import { AuthInterceptor } from './interceptor';
import { LangInterceptor } from './langinterceptor';
import { SpinnerInterceptor } from './spinnerinterceptor';
import { ImageCropperModule } from "ngx-image-cropper";

import * as Raven from 'raven-js';
import { environment } from '../environments/environment';
import { ErrorHandler } from '@angular/core';


registerLocaleData(localeNL);
registerLocaleData(localeDE);
registerLocaleData(localeFR);


export function configServiceFactory(config: Config) {
  return () => config.load();
}

export function authServiceFactory(auth: AuthService) {
  return () => auth.load();
}

/*export function langServiceFactory(lang: LanguageService) {
  return () => lang.load();
}*/

if (environment.production) {
  Raven
    .config('https://492c14aba799487696503fc4c1d9389c@sentry.pythonunited.com//8')
    .install();
}

export class RavenErrorHandler implements ErrorHandler {
  handleError(err:any) : void {
    Raven.captureException(err);
  }
}

@NgModule({
  declarations: [
    HomeComponent,
    AppComponent,
    NavComponent,
    AlertComponent,
    CartDetailComponent,
    ProductListComponent,
    HighlightsComponent,
    ProductDetailComponent,
    ProductPreviewComponent,
    ProductSummaryComponent,
    ProductCreateComponent,
    ProductUpdateComponent,
    ProductFilterComponent,
    ProductPaginationComponent,
    ProductSearchComponent,
    ActiveFilterComponent,
    BrandListComponent,
    BrandDetailComponent,
    CategoryListComponent,
    CollectionListComponent,
    LoginComponent,
    RequestPwdComponent,
    PasswordResetComponent,
    ProfileComponent,
    AddressComponent,
    InboxComponent,
    ProfilePasswordComponent,
    ProfileOrdersComponent,
    ProfileBankaccountComponent,
    RegisterComponent,
    ImageFieldComponent,
    PageComponent,
    PageNotFoundComponent,
    CheckoutComponent,
    OrderComponent,
    SpinnerComponent,
    CarouselComponent,
    FooterComponent,
    TranslatePipe,
    NoSanitizePipe,
    OnlyNumber,
    RouteDirective,
  ],
  imports: [
    NgbModule.forRoot(),
    CoreModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    RouterModule.forRoot(
      AppRoutes,
      //{ enableTracing: true }
    ),
    Ng2ImgMaxModule,
    ImageCropperModule,
  ],
  providers: [
    ProductService,
    ProductFilterService,
    ProductDetailService,
    BrandService,
    CollectionService,
    ColorService,
    SizeService,
    StateService,
    FabricService,
    PageService,
    PageImageService,
    CategoryService,
    UserService,
    LanguageService,
    MessageService,
    SpinnerService,
    ImageUtils,
    GoogleAnalyticsEventsService,
    MetaService,
    {
      provide: APP_INITIALIZER,
      useFactory: configServiceFactory,
      deps: [Config],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authServiceFactory,
      deps: [AuthService],
      multi: true
    },
    /*{
      provide: APP_INITIALIZER,
      useFactory: langServiceFactory,
      deps: [LanguageService],
      multi: true
    },*/
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LangInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true
    },
    environment.production ? {
      provide: ErrorHandler,
      useClass: RavenErrorHandler,
    } : [],
  ],
  bootstrap: [AppComponent]
})

export class AppModule { }



// WEBPACK FOOTER //
// ./src/app/app.module.ts